<template>
<transition name="modal">
<div class="modal-container">
  <div class="modal-backdrop"></div>
  <div class="modal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="title">遅延連絡</h2>
        </div>
        <div class="modal-body" ref="modal">
          <div v-show="isInputStep">
            <p class="section-title">到着予定時刻を選択</p>
            <div class="time-picker">
              <select v-model="selectedMonth">
                <option v-for="month in monthOptions" :key="month" :value="month">
                  {{ month }}
                </option>
              </select>
              <span class="time-label">月</span>
              <select v-model="selectedDay">
                <option v-for="day in dayOptions" :key="day" :value="day">
                  {{ day }}
                </option>
              </select>
              <span class="time-label">日</span>
              <input
                v-model.trim="selectedHour"
                class="time-input"
                type="text"
                inputmode="numeric"
                maxlength="2"
              />
              <span class="time-label">：</span>
              <input
                v-model.trim="selectedMinute"
                class="time-input"
                type="text"
                inputmode="numeric"
                maxlength="2"
              />
            </div>
            <div class="err-msg-area" v-show="hasError">
              <span class="iferror" v-show="errors.date_required">
                ▲ 到着予定時刻を入力してください
              </span>
              <span class="iferror" v-show="errors.date_after_required">
                ▲ 到着予定時刻は予定時刻より後にしてください
              </span>
            </div>
            <div class="modal-footer">
              <button class="submit1 bg_gray modal-btn"
                @click="$emit('dismiss')">
                キャンセル
              </button>
              <button class="submit1 bg_orange modal-btn btn-primary"
                @click="nextStep()">
                確認画面
              </button>
            </div>
          </div>
          <div v-show="isConfirmStep">
            <p class="section-title">
              到着予定時刻がお客様に送信されます。以下で間違いないですか？
            </p>
            <p class="planned-time">{{ plannedTimeLabel }}</p>
            <div class="err-msg-area" v-show="sendError">
              <span class="iferror">
                ▲ 送信に失敗しました。再度お試しください
              </span>
            </div>
            <div class="modal-footer">
              <button class="submit1 bg_gray modal-btn"
                @click="prevStep()">
                戻る
              </button>
              <button class="submit1 bg_orange modal-btn btn-primary"
                @click="sendNotice()">
                送信する
              </button>
            </div>
          </div>
          <div v-show="isCompleteStep">
            <p class="section-title">送信されました</p>
            <div class="modal-footer">
              <button class="submit1 bg_green modal-btn btn-primary"
                @click="$emit('backToList')">
                集配一覧に戻る
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</transition>
</template>

<script>
import Vue from 'vue'
import adminWashRequestApi from '@/api/adminWashRequest'
import { StatusKey } from '@/consts/washRequest'

const StepKey = {
  INPUT_STEP: 'inputStep',
  CONFIRM_STEP: 'confirmStep',
  COMPLETE_STEP: 'completeStep',
}

const StepOrder = [
  StepKey.INPUT_STEP,
  StepKey.CONFIRM_STEP,
  StepKey.COMPLETE_STEP,
]

export default {
  name: 'admin-wash-request-delay-notice-modal',
  props: {
    washRequest: {
      type: Object,
      default () {
        return {}
      }
    },
    initialStep: {
      type: String,
      default: StepKey.INPUT_STEP,
    }
  },
  data() {
    return {
      currentStep: StepOrder.includes(this.initialStep)
        ? this.initialStep
        : StepKey.INPUT_STEP,

      selectedMonth: '',
      selectedDay: '',
      selectedHour: '',
      selectedMinute: '',
      errors: {},
      sendError: false,
    }
  },
  mounted() {
    this.setInitialDate()
  },
  computed: {
    hasError() {
      return Object.keys(this.errors).length > 0
    },
    monthOptions() {
      return Array.from({ length: 12 }, (_, index) => index + 1)
    },
    dayOptions() {
      const baseDate = this.parseDateString(this.getInitialDateString()) || new Date()
      const year = baseDate.getFullYear()
      const month = this.selectedMonth || 1
      const daysInMonth = new Date(year, month, 0).getDate()
      return Array.from({ length: daysInMonth }, (_, index) => index + 1)
    },
    plannedTimeLabel() {
      const month = this.selectedMonth || '--'
      const day = this.selectedDay || '--'
      const hour = this.isValidHour(this.selectedHour) ? this.padTime(this.selectedHour) : '--'
      const minute = this.isValidMinute(this.selectedMinute) ? this.padTime(this.selectedMinute) : '--'
      return `${month}月${day}日 ${hour}:${minute}`
    },
    isInputStep() {
      return this.currentStep === StepKey.INPUT_STEP
    },
    isConfirmStep() {
      return this.currentStep === StepKey.CONFIRM_STEP
    },
    isCompleteStep() {
      return this.currentStep === StepKey.COMPLETE_STEP
    },
  },
  methods: {
    nextStep() {
      if (!this.validateDateSelection()) { return }
      const currentIndex = StepOrder.indexOf(this.currentStep)
      const nextIndex = Math.min(currentIndex + 1, StepOrder.length - 1)
      this.currentStep = StepOrder[nextIndex]
      this.updateStep()
    },
    prevStep() {
      this.sendError = false
      const currentIndex = StepOrder.indexOf(this.currentStep)
      const prevIndex = Math.max(currentIndex - 1, 0)
      this.currentStep = StepOrder[prevIndex]
      this.updateStep()
    },
    sendNotice() {
      this.sendError = false
      this.saveRescheduledDate()
    },
    updateStep() {
      this.$refs.modal.scrollTop = 0
    },
    async saveRescheduledDate() {
      if (!this.washRequest || !this.washRequest.id) {
        this.sendError = true
        return
      }

      const params = {
        rescheduled_dt: this.buildRescheduledDateString(),
      }

      try {
        await adminWashRequestApi.setRescheduledDate(this.washRequest.id, params)
        this.currentStep = StepKey.COMPLETE_STEP
        this.updateStep()
        this.$emit('updated')
      } catch (error) {
        console.error('Failed to set rescheduled date:', error)
        this.sendError = true
      }
    },
    validateDateSelection() {
      this.errors = {}

      if (
        !this.selectedMonth ||
        !this.selectedDay ||
        !this.isValidHour(this.selectedHour) ||
        !this.isValidMinute(this.selectedMinute)
      ) {
        this.errors.date_required = true
        return false
      }

      const baseDateString = this.getInitialDateString()
      const baseDate = this.parseDateString(baseDateString)
      if (!baseDate) { return true }

      const selectedDate = new Date(
        baseDate.getFullYear(),
        this.selectedMonth - 1,
        this.selectedDay,
        Number(this.selectedHour),
        Number(this.selectedMinute)
      )

      if (Number.isNaN(selectedDate.getTime()) || selectedDate <= baseDate) {
        this.errors.date_after_required = true
        return false
      }

      return true
    },
    setInitialDate() {
      const dateString = this.getInitialDateString()
      if (!dateString) { return }

      const date = this.parseDateString(dateString)
      if (!date) { return }

      this.selectedMonth = date.getMonth() + 1
      this.selectedDay = date.getDate()
      this.selectedHour = ''
      this.selectedMinute = ''
    },
    getInitialDateString() {
      if (!this.washRequest || this.washRequest.status_key === undefined) { return '' }
      if (this.washRequest.status_key === StatusKey.CREATED) {
        return this.washRequest.sched_pickup_dt || ''
      }
      if (this.washRequest.status_key === StatusKey.FOLDED) {
        return this.washRequest.sched_delivery_dt || ''
      }
      return ''
    },
    buildRescheduledDateString() {
      const baseDate = this.parseDateString(this.getInitialDateString()) || new Date()
      const rescheduledDate = new Date(
        baseDate.getFullYear(),
        this.selectedMonth - 1,
        this.selectedDay,
        Number(this.selectedHour),
        Number(this.selectedMinute)
      )
      const dtFormat = Vue.filter('dtFormat')
      return dtFormat(rescheduledDate, 'yyyy-mm-dd HH:MM:SS')
    },
    parseDateString(dateString) {
      if (!dateString) { return null }
      const normalized = dateString.replace(' ', 'T')
      const date = new Date(normalized)
      if (Number.isNaN(date.getTime())) { return null }
      return date
    },
    isValidHour(value) {
      if (value === '') { return false }
      if (!/^\d{1,2}$/.test(String(value))) { return false }
      const hour = Number(value)
      return Number.isInteger(hour) && hour >= 0 && hour <= 23
    },
    isValidMinute(value) {
      if (value === '') { return false }
      if (!/^\d{1,2}$/.test(String(value))) { return false }
      const minute = Number(value)
      return Number.isInteger(minute) && minute >= 0 && minute <= 59
    },
    padTime(value) {
      return String(value).padStart(2, '0')
    },
  },
  watch: {
    selectedMonth() {
      const maxDay = this.dayOptions[this.dayOptions.length - 1]
      if (this.selectedDay && this.selectedDay > maxDay) {
        this.selectedDay = ''
      }
    }
  },
}
</script>

<style lang="scss" scoped>
  .modal-container {
    font-size: 18px;
  }
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    outline: 0;
    background-color: #000000;
    opacity: 0.3;
  }
  .modal {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 5;
  }
  .modal-dialog {
    background-color: #ffffff;
    margin: 1.75rem auto;
    max-width: 90%;
    max-height: 90%;
    border-radius: 4px;
    overflow: hidden;
  }
  @media screen and (min-width: 800px){
    .modal-dialog {
      max-width: 70%;
    }
  }
  .modal-header {
    text-align: center;
    .title {
      padding: 0;
      line-height: 3.4;
      font-size: 1.2rem;
    }
  }
  .modal-body {
    padding: 16px 24px 24px;
  }
  .section-title {
    text-align: center;
    margin: 8px 0 16px;
  }
  .time-picker {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin: 16px 0 24px;
  }
  .time-picker select {
    width: 70px;
  }
  .time-input {
    width: 70px;
    height: 32px;
    padding: 4px 6px;
    text-align: center;
  }
  .time-label {
    font-weight: bold;
  }
  .planned-time {
    text-align: center;
    font-size: 1.1rem;
    margin: 16px 0 24px;
  }
  .err-msg-area {
    margin: 10px auto 0;
    text-align: center;
    width: 90%;
  }
  .modal-footer {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 8px;
  }
  .modal-btn {
    width: 160px;
    margin: 0 4px;
    padding: 8px 4px;
    height: 42px;
  }
  .modal-enter {
    opacity: 0;
  }
  .modal-enter-active {
    transition: opacity .0s ease;
  }
  .modal-enter-to {
    opacity: 1.0;
  }
  .modal-leave-active {
    opacity: 0;
  }
</style>
